import { Grid, Typography } from '@material-ui/core';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import ProductCodeContainer from 'src/common/components/code-container/components/product-code-container';
import {
  restorePage,
  setHomePage,
} from 'src/common/components/emerald-home-page/emerald-home-page';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { AppState, store } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { waitForCardDetails } from 'src/devices/payment/get-card-details';
import {
  beginPaymentSession,
  cancelPinpadOperation,
  endPaymentSession,
  inPaymentSession,
} from 'src/devices/payment/pinpad-operations';
import { voidEmptyTransaction } from 'src/home-page/business-logic/void-transaction';
import { initiateTransaction } from 'src/items-list/business-logic/initiate-transaction';
import Keypad from 'src/keypad/components/keypad';
import { popupAsyncMessage } from 'src/messages/business-logic/popup-message';
import { codeContainerStyles } from '../common/components/code-container/components/code-container-styles';
import barcode from '../common/media/barcode.svg';
import card from '../common/media/CreditDebit.svg';
import keypad from '../common/media/keypad.svg';
import { handleReloadItemEntry } from './buiness-logic/handle-reload-item-entry';

export const showReloadItem = async (): Promise<void> => {
  const hasTransaction = !!store.getState().transaction.transactionId;
  if (!hasTransaction) {
    await initiateTransaction();
  }
  setHomePage(<ReloadItemContainer voidOnCancel={!hasTransaction} />, data =>
    withSpinner(async () => {
      await handleReloadItemEntry(data, EntryMethod.Scanned, !hasTransaction);
      onExit();
    }),
  );
};

const onExit = () => restorePage();

export const ReloadItemContainer: React.FC<ReloadItemContainerProps> = ({
  voidOnCancel,
}): JSX.Element => {
  const { t } = useTranslation();
  const keypadSubmitButton = {
    label: 'OK',
    onClick: data =>
      withSpinner(async () => {
        await handleReloadItemEntry(data, EntryMethod.Keyed, voidOnCancel);
        onExit();
      }),
  };

  const onCancel = async (): Promise<void> => {
    await voidEmptyTransaction();
    onExit();
  };

  const hasPinPad = useSelector((state: AppState) => state.pinPad.isPinpadConfigured);

  useEffect(() => {
    let needsCancel = true;
    (async () => {
      if (hasPinPad) {
        if (inPaymentSession()) {
          await cancelPinpadOperation();
          await endPaymentSession();
        }
        await beginPaymentSession();
        const details = await waitForCardDetails(1, 1)
          .catch(() => {
            // Do nothing for this flow
            return undefined;
          })
          .finally(() => {
            needsCancel = false;
          });
        const cardNumber = details?.card.pan;
        if (cardNumber) {
          withSpinner(async () => {
            await handleReloadItemEntry(cardNumber, EntryMethod.MSR, voidOnCancel);
            onExit();
          });
        } else if (details) {
          // Edge case for this working but we can't read card data, tell the cashier about it
          await popupAsyncMessage({
            message: 'OnlineItemCardNumberError',
          });
        }
      }
    })();

    return () => {
      if (hasPinPad && needsCancel) {
        cancelPinpadOperation();
      }
    };
  }, [hasPinPad, voidOnCancel]);

  return (
    <ProductCodeContainer
      headerTitle={t('TopUpCommand')}
      onKeypadSubmit={keypadSubmitButton.onClick}
      keypadComponent={
        <Keypad
          keyboardId='reload-item'
          disableManualWeight
          disableQuantity
          topLabelText=' '
          submitButton={keypadSubmitButton}
          visible
          keypadTestIdPrefix='reload-item-'
        />
      }
    >
      <ReloadItem onCancel={onCancel} />
    </ProductCodeContainer>
  );
};

const ReloadItem = (props: ReloadItemProps): JSX.Element => {
  const classes = codeContainerStyles();
  const trainingModeClasses = trainingModeStyles();
  const { t } = useTranslation();
  const trainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);

  return (
    <Grid
      item
      className={`fullHeight flexFull ${classes.outerMostContainer}`}
      data-testid='reload-item'
    >
      <Grid
        item
        className={`${
          trainingMode ? trainingModeClasses.trainingModeBackground : classes.background
        } fullWidth fullHeight`}
      >
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.outerFlexDesign}`}
          data-testid='reload-item-scanning-container'
        >
          <Grid
            item
            container
            className={classes.flexDesign}
            data-testid='reload-item-body-container'
          >
            <Grid item container className={classes.containerAlignment}>
              <Grid className={classes.spacing}>
                <Typography variant={'h4'} data-testid='reload-item-body-text1'>
                  {t('ItemScanMessage1')}
                </Typography>
              </Grid>
              <Grid
                container
                justifyContent='center'
                className={classes.spacing}
                data-testid='reload-item-icon-1'
              >
                <Grid className={classes.iconPadding}>
                  <img src={barcode} alt='barcode' className={classes.icon} />
                </Grid>
                <img src={card} alt='card' className={classes.icon} />
              </Grid>
              <Grid container justifyContent='center' className={classes.spacing}>
                <Typography variant={'h4'} data-testid='reload-item-body-text2'>
                  - {t('ItemScanMessage2')} -
                </Typography>
              </Grid>
              <Grid container justifyContent='center' className={classes.spacing}>
                <Typography variant={'h4'} data-testid='reload-item-body-text3'>
                  {t('ItemScanMessage3')}
                </Typography>
              </Grid>
              <Grid
                container
                justifyContent='center'
                className={classes.spacing}
                data-testid='barcode-pinpad-icon-container'
              >
                <img src={keypad} alt='keypad' className={classes.icon} />
              </Grid>
            </Grid>
            <Grid item container justifyContent='center' className={classes.selfAlign}>
              <BaseButton
                buttonType='white'
                onClick={props.onCancel}
                data-testid='reload-item-cancel-button'
              >
                <Typography variant={'button'}>{t('Cancel')}</Typography>
              </BaseButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ReloadItem;

export interface ReloadItemProps {
  onCancel: () => void;
}

interface ReloadItemContainerProps {
  voidOnCancel: boolean;
}
